/**
 * RESPONSIBILITY: the @Img control - upload, preview, alt text.
 *
 * Registers with a tester + rank. The highest-ranked renderer for a schema node
 * wins, so adding a control is a registration — there is no switch on field type.
 */
import { useState } from 'react';
import { rankWith, optionIs, type ControlProps } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import { EMPTY_IMAGE, hasImage, publicPath, formatBytes, type ImageValue } from '@framework/core';
import { uploadMedia } from '../upload-media';
import { useMagnify, MAX_ZOOM } from './magnify';

function ImageControlBase({ data, handleChange, path, label, uischema, errors }: ControlProps) {
  const opts = (uischema as { options?: Record<string, unknown> }).options ?? {};
  const folder = String(opts.folder ?? 'misc');
  const maxSizeMb = Number(opts.maxSizeMb ?? 4);
  const size = opts.size ? String(opts.size) : '';
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const zoom = useMagnify();
  /** What the server just told us. Only ever about the file uploaded in THIS session. */
  const [justUploaded, setJustUploaded] = useState<{ name: string; bytes: number } | null>(null);

  const img: ImageValue = { ...EMPTY_IMAGE, ...(data as Partial<ImageValue> | undefined) };

  async function upload(file: File) {
    setBusy(true); setErr(null);
    const r = await uploadMedia(file, folder, maxSizeMb, 'image');
    setBusy(false);

    if (!r.ok) { setErr(r.error); return; }
    // The document keeps the reference and its own alt text, nothing else about the file.
    setJustUploaded({ name: r.value.originalName, bytes: r.value.bytes });
    handleChange(path, { path: r.value.path, alt: img.alt });
  }

  return (
    <div className="ctl">
      {/* The size belongs ON the label, not in a hint underneath it: it is part of what
          is being asked for, and it has to be readable before the file picker opens —
          afterwards is too late to have chosen a different photograph. */}
      <label className="ctl-label">
        {label}
        {size && <span className="ctl-size">{size}</span>}
      </label>
      {/* [image] [alt]
                  [icons + metadata] */}
      <div className="ctl-img">
        {/* The image IS the picker. The file input covers it at zero opacity, so the
            click target is the picture and the control stays keyboard-reachable —
            display:none would take it out of the tab order entirely. */}
        <label
          ref={zoom.box} style={zoom.style}
          className={zoom.big ? 'ctl-img-pick is-big' : 'ctl-img-pick'}
          title={hasImage(img) ? 'Click to replace' : 'Click to choose'}
        >
          {hasImage(img)
            ? <img src={img.path} alt={img.alt} className="ctl-img-preview" />
            : <span className="ctl-img-empty">no image</span>}
          <span className="ctl-img-veil">{busy ? 'Uploading…' : hasImage(img) ? 'Replace' : 'Choose'}</span>
          <input
            type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml,image/avif"
            disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }}
          />
        </label>

        <input
          type="text" className="ctl-input" placeholder="Alt text (for screen readers)"
          value={img.alt}
          onChange={(e) => handleChange(path, { ...img, alt: e.target.value })}
        />

        {hasImage(img) && (
          <div className="ctl-img-bar">
            <div className="ctl-img-icons">
              <button type="button" className="ctl-img-icon ctl-img-zoom"
                      aria-pressed={zoom.big} onClick={zoom.toggle}
                      title={zoom.big ? 'Back to normal size' : `Magnify (${MAX_ZOOM}×)`}
                      aria-label={zoom.big ? 'Back to normal size' : 'Magnify'}>
                {zoom.big ? '⊖' : '⊕'}
              </button>
              {/* `download` is always present: without it the browser NAVIGATES to the file
                  and a picture opens in a tab instead of saving. `true` lets the browser
                  name it from the address when there is no original filename to offer. */}
              <a className="ctl-img-icon ctl-img-get" href={`${img.path}?original`}
                 download={justUploaded?.name || true}
                 title="Download" aria-label="Download image">↓</a>
              <button type="button" className="ctl-img-icon ctl-img-drop"
                      onClick={() => handleChange(path, { ...EMPTY_IMAGE })}
                      title="Remove" aria-label="Remove image">✕</button>
            </div>

            <div className="ctl-img-meta">
              {justUploaded && <span className="ctl-img-name">{justUploaded.name}</span>}
              {justUploaded && <span>{formatBytes(justUploaded.bytes)}</span>}
              <span className="ctl-img-path" title={publicPath(img)}>{publicPath(img)}</span>
            </div>
          </div>
        )}

        {err && <span className="ctl-error ctl-img-err">{err}</span>}
      </div>
      {errors && <p className="ctl-error">{errors}</p>}
    </div>
  );
}

export const imageControl = {
  // Objects now, not strings — the tester must not require a string control.
  tester: rankWith(10, optionIs('widget', 'image')),
  renderer: withJsonFormsControlProps(ImageControlBase),
};
